import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@redux/store";
import { rentSelector } from "@redux/rent/selectors";
import { fetchGetArenas } from "@redux/rent/asyncActions";
import { setDate } from "@redux/rent/slice";
import { getRentDateFromStorage } from "@/utils";
import { moscowNow } from "@/utils/date/moscowNow";
import dateFormat from "dateformat";
import { Header, Title } from "@/components";
import { WeekDays } from "../WeekDays";
import { DateNavigation } from "../DateNavigation";
import { ArenaSkeleton } from "./ArenaSkeleton";
import { sortArenaTitles } from "@/utils/courtRent/arenaNames";
import styles from "./ChoosingRoom.module.scss";
import arrowRightIcon from "@assets/icons/arrowCalendarRightGreen.svg";

interface ChoosingRoomProps {
  title: string;
}

export const ChoosingRoom: React.FC<ChoosingRoomProps> = ({ title }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { arenas, date, isLoading, isBackgroundLoading } = useAppSelector(rentSelector);

  // Просматриваемая дата (для навигации по месяцам, без выбора дня)
  const [viewedDate, setViewedDate] = useState<string>("");

  useEffect(() => {
    const storageDate = getRentDateFromStorage();
    if (storageDate) {
      dispatch(setDate(storageDate));
    } else {
      dispatch(setDate(dateFormat(moscowNow(), "isoDate")));
    }
  }, [dispatch]);

  // Синхронизируем viewedDate при изменении выбранного дня
  useEffect(() => {
    if (date) {
      setViewedDate(date);
    }
  }, [date]);

  useEffect(() => {
    if (date) {
      dispatch(fetchGetArenas({ date }));
    }
  }, [date, dispatch]);

  const handleMonthChange = (newViewedDate: string) => {
    setViewedDate(newViewedDate);
  };

  const arenaTypes = React.useMemo(() => {
    const typesMap = new Map<string, number>();

    arenas.forEach((arena) => {
      if (arena.title) {
        const currentCount = typesMap.get(arena.title) || 0;
        typesMap.set(arena.title, currentCount + (arena.courts?.length || 0));
      }
    });
    
    const typesWithCount = Array.from(typesMap.entries()).map(([title, count]) => ({
      title,
      count
    }));
    
    const getOrder = (title: string) => {
      if (title === 'Хард первый корпус') return 1;
      if (title === 'Хард второй корпус') return 2;
      if (title.toLowerCase().includes('padel')) return 3;
      if (title.toLowerCase().includes('стенк')) return 4;
      if (title.toLowerCase().includes('зал')) return 5;
      return 999;
    };

    return typesWithCount.sort((a, b) => {
      const orderA = getOrder(a.title);
      const orderB = getOrder(b.title);

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      return a.title.localeCompare(b.title);
    });
  }, [arenas]);

  const handleSelectArena = (arenaTitle: string) => {
    navigate("/time-and-court", { state: { selectedArenaTitle: arenaTitle } });
  };

  const handleViewScheme = () => {
    navigate("/about/court-layout");
  };

  return (
    <>
      <Header title={title} />
      <main>
        <section className={`container ${styles.section}`}>
          <Title>{title}</Title>
          <p className={styles.description}>
            Выберите помещение
          </p>

          <div className={styles.wrapper}>
            <DateNavigation viewedDate={viewedDate} onMonthChange={handleMonthChange} />

            <WeekDays date={viewedDate} selectedDate={date} />

            {isLoading ? (
              <ArenaSkeleton />
            ) : (
              <div className={styles.arenaList}>
                {arenaTypes.length === 0 && !isBackgroundLoading ? (
                  <div className={styles.emptyState}>
                    Нет доступных типов кортов
                  </div>
                ) : (
                  <div className={styles.arenaListWrapper}>
                    {arenaTypes.map((arenaType) => {
                      const getCourtWord = (count: number) => {
                        const lastDigit = count % 10;
                        const lastTwoDigits = count % 100;

                        if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return 'кортов';
                        if (lastDigit === 1) return 'корт';
                        if (lastDigit >= 2 && lastDigit <= 4) return 'корта';
                        return 'кортов';
                      };

                      const displayTitle = arenaType.title.toLowerCase().includes('padel')
                        ? `Padel ${arenaType.count} ${getCourtWord(arenaType.count)}`
                        : arenaType.title;

                      return (
                        <button
                          key={arenaType.title}
                          className={styles.arenaItem}
                          onClick={() => handleSelectArena(arenaType.title)}
                        >
                          <span className={styles.arenaItemText}>
                            {displayTitle}
                          </span>
                          <img src={arrowRightIcon} alt="arrow" className={styles.arenaItemArrow} />
                        </button>
                      );
                    })}

                    <button className={styles.schemeLink} onClick={handleViewScheme}>
                      Посмотреть схему кортов и залов
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
};
